import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { birrInputValue, formatPhone, newId, normalizeEthiopianPhone, tryParseBirr } from '@dairy/shared';
import { MoneyInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Button, Card, Checkbox, Field, Input, PageHeader, Textarea } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { save } from '@/db/repo';

/** IN-1 buyer form (owner): name, phone, agreed price per liter, active, note. A price change affects only new sales. */
export function BuyerFormPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const existing = useLiveQuery(() => (id ? db.buyers.get(id) : undefined), [id]);
  const buyers = useLiveQuery(() => db.buyers.toArray(), []) ?? [];
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [price, setPrice] = useState('');
  const [active, setActive] = useState(true);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setPhone(formatPhone(existing.phone).replace(/\s/g, ''));
    setPrice(birrInputValue(existing.pricePerLiterSantim));
    setActive(existing.active);
    setNote(existing.note ?? '');
  }, [existing]);

  const submit = async () => {
    const p = tryParseBirr(price);
    if (!name.trim()) return setError(t('money.errors.name'));
    if (!p.ok || !p.santim || p.santim < 100 || p.santim > 100_000) return setError(t('money.errors.price'));
    const ph = phone.trim() ? normalizeEthiopianPhone(phone) : null;
    if (phone.trim() && !ph) return setError(t('money.errors.phone'));
    if (active && buyers.some((b) => b.id !== id && b.active && b.name.trim().toLowerCase() === name.trim().toLowerCase())) {
      return setError(t('errors.CONFLICT_UNIQUE'));
    }
    try {
      const saved = await save('buyer', { ...(existing ?? {}), id: id ?? newId(), name: name.trim(), phone: ph, pricePerLiterSantim: p.santim, active, note: note.trim() || null });
      toast(t('common.saved'));
      navigate(`/buyers/${saved}`, { replace: true });
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <PageHeader title={id ? t('money.editBuyer') : t('money.addBuyer')} />
      <Card className="space-y-4">
        <Field label={t('money.buyerName')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </Field>
        <Field label={t('money.phoneOptional')}>
          <Input inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="09XXXXXXXX" />
        </Field>
        <Field label={t('money.agreedPrice')} hint={id ? t('money.priceChangeHint') : undefined}>
          <MoneyInput value={price} onChange={(e) => setPrice(e.target.value)} />
        </Field>
        <Checkbox label={t('common.active')} checked={active} onChange={(e) => setActive(e.target.checked)} />
        <Field label={t('common.note')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </Field>
        {error ? <p className="text-red-700">{error}</p> : null}
        <Button size="lg" className="w-full" onClick={() => void submit()}>
          {t('common.save')}
        </Button>
      </Card>
    </div>
  );
}
